import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styles from "./styles/EventDetailPage.module.css";
// import { getPublicEventDetail, getPublicEvents } from "../apis";
import { defaultAxios } from "./apis";

function formatDayTitle(iso, tz) {
  const d = new Date(iso);
  const weekday = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    timeZone: tz || undefined,
  }).format(d);

  const monthDayYear = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: tz || undefined,
  }).format(d);

  return `${weekday}, ${monthDayYear}`;
}

function formatTimeRange(startISO, endISO, tz) {
  if (!startISO) return "";
  const start = new Date(startISO);
  const end = endISO ? new Date(endISO) : null;

  const tf = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: tz || undefined,
  });

  const s = tf.format(start);
  const e = end ? tf.format(end) : null;
  return e ? `${s}–${e}` : s;
}

export default function EventDetailPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [markdown, setMarkdown] = useState("");
  const [upcoming, setUpcoming] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        // const res = await getPublicEventDetail(slug);
        const res = await defaultAxios.get(`/api/events/${slug}/`);
        if (!alive) return;

        const ev = res.data?.event || res.data;
        setEvent(ev);
        setMarkdown(res.data?.markdown || ev?.markdown || "");

        const list = await defaultAxios.get("/api/events/");
        if (!alive) return;

        const other = (list.data?.upcoming || []).filter((x) => x.slug !== slug);
        setUpcoming(other[0] || null);
      } catch (e) {
        if (!alive) return;
        setEvent(null);
        setMarkdown("");
        setUpcoming(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [slug]);

  const tz = event?.timezone_str;

  const dateLabel = useMemo(() => {
    if (!event?.start_at) return "";
    return formatDayTitle(event.start_at, tz);
  }, [event, tz]);

  if (loading) return <div className={styles.page}>Loading event…</div>;
  if (!event) return <div className={styles.page}>Event not found.</div>;

  const isOnline = event.event_type === "ONLINE";

  return (
    <div className={styles.page}>
    <div className={styles.heroBleed}>
      <div className={styles.hero}>
        {event.hero_image ? (
          <img src={event.hero_image} alt={event.title} />
        ) : (
          <div className={styles.heroPlaceholder} />
        )}
      </div>
    </div>

      <Link className={styles.back} to="/events">
        ← Go Back To Event
      </Link>

      <div className={styles.grid}>
        <div className={styles.body}>
          <h2 className={styles.h2}>Description/About Event</h2>

          <div className={styles.markdown}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {markdown || ""}
            </ReactMarkdown>
          </div>

          {Array.isArray(event.speakers) && event.speakers.length > 0 && (
            <>
              <h3 className={styles.h3}>Speakers</h3>
                <div className={styles.speakersGrid}>
                  {event.speakers.map((sp, idx) => (
                    <div key={idx} className={styles.speakerCard}>
                      <div className={styles.speakerAvatar}>
                        {sp.photo ? (
                          <img src={sp.photo} alt={sp.name} />
                        ) : (
                          <div className={styles.avatarPlaceholder} />
                        )}
                      </div>

                      <div className={styles.speakerInfo}>
                        <div className={styles.speakerName}>{sp.name}</div>
                        {sp.role ? <div className={styles.speakerRole}>{sp.role}</div> : null}
                        {sp.tagline ? <div className={styles.speakerTag}>{sp.tagline}</div> : null}
                      </div>
                    </div>
                  ))}
                </div>
            </>
          )}

          {Array.isArray(event.what_you_get) && event.what_you_get.length > 0 && (
            <>
              <h3 className={styles.h3}>What will you get?</h3>
              <ul className={styles.list}>
                {event.what_you_get.map((x, idx) => (
                  <li key={idx}>{x}</li>
                ))}
              </ul>
            </>
          )}
          <h3 className={styles.h3}>Ticket</h3>
          <div className={styles.ticket}>
            {event.is_free
              ? "FREE"
              : event.currency === "USD"
                ? `$${event.ticket_price}`
                : `${event.ticket_price} ${event.currency || ""}`
            }
            {event.ticket_note ? ` — ${event.ticket_note}` : ""}
          </div>
        </div>

        <div className={styles.side}>
          <div className={styles.sideCard}>
            <div className={styles.sideTitle}>{dateLabel}</div>
            <div className={styles.sideMeta}>
              <div>
                ⏱ {formatTimeRange(event.start_at, event.end_at, tz)}{" "}
                {tz ? `(${tz})` : ""}
              </div>
              <div>
                📍{" "}
                {isOnline ? (
                  <>
                    {event.platform || "Online"}
                    {event.meeting_url ? (
                      <span className={styles.muted}> (send by email)</span>
                    ) : null}
                  </>
                ) : (
                  <>
                    {event.location_name || "Venue"}
                    {event.location_address
                      ? `, ${event.location_address}`
                      : ""}
                  </>
                )}
              </div>
            </div>

            <div className={styles.ctaRow}>
              {event.register_url ? (
                <a
                  className={styles.btnPrimary}
                  href={event.register_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {event.register_text ||
                    (event.is_free ? "Register for Free" : "Register Here")}{" "}
                  <span>↗</span>
                </a>
              ) : (
                <button className={styles.btnPrimary} disabled>
                  Register
                </button>
              )}
            </div>
          </div>

          <div className={styles.upcoming}>
            <div className={styles.upcomingTitle}>Upcoming Event</div>
            {upcoming ? (
              <div className={styles.upcomingItem}>
                <div className={styles.upcomingName}>{upcoming.title}</div>
                <div className={styles.upcomingSub}>
                  {formatDayTitle(upcoming.start_at, upcoming.timezone_str)},{" "}
                  {formatTimeRange(
                    upcoming.start_at,
                    upcoming.end_at,
                    upcoming.timezone_str
                  )}{" "}
                  {upcoming.timezone_str ? `(${upcoming.timezone_str})` : ""}
                </div>
                <Link className={styles.btnGhostSmall} to={`/events/${upcoming.slug}`}>
                  View Event
                </Link>
              </div>
            ) : (
              <div className={styles.muted}>No upcoming events.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
